/**
 * Formato canónico de los teléfonos de la plataforma: prefijo de país y número
 * en dígitos, sin '+' (E.164 tal y como lo quieren la Graph API de WhatsApp y
 * Altiria para los SMS). Es el mismo criterio que aplica el back en
 * `User::normalizePhone`; si cambia uno, hay que cambiar el otro.
 */

/** Móvil o fijo español tal y como lo teclea la gente: 9 cifras, sin prefijo. */
const SPANISH_NATIONAL = /^[6789]\d{8}$/;

/** El mismo número ya en canónico. */
const SPANISH_FULL = /^34[6789]\d{8}$/;

/**
 * Deja solo dígitos y quita el prefijo internacional marcado a la europea
 * ('0034 600...' → '34600...'). No añade nada: no adivina el país.
 */
export function normalizeDigits(raw: string | null | undefined): string {
  const digits = (raw ?? '').replace(/\D/g, '');
  return digits.startsWith('00') ? digits.slice(2) : digits;
}

export function isSpanishNational(digits: string): boolean {
  return SPANISH_NATIONAL.test(digits);
}

export function isSpanishFull(digits: string): boolean {
  return SPANISH_FULL.test(digits);
}

/**
 * Las 9 cifras nacionales de un número español completo, o null si el número
 * no es español (o no se puede afirmar que lo sea). Sirve para pintar el
 * prefijo aparte sin tocar los números extranjeros.
 */
export function splitSpanish(digits: string): string | null {
  return isSpanishFull(digits) ? digits.slice(2) : null;
}

/**
 * Las 9 cifras nacionales de un número español, venga en canónico
 * ('34600112233') o desnudo ('600112233'); null si no se puede afirmar que sea
 * español. Hace falta la segunda forma porque en base de datos conviven las dos
 * hasta que se lance el backfill que las unifica.
 */
export function spanishNationalPart(digits: string): string | null {
  if (isSpanishFull(digits)) {
    return digits.slice(2);
  }

  return isSpanishNational(digits) ? digits : null;
}

/**
 * Lo que debe quedar en un campo que ya muestra el prefijo '+34' al lado:
 * si el usuario pega el número completo ('+34 686 488 031', '0034686488031'),
 * se queda solo con la parte nacional en vez de duplicar el prefijo.
 */
export function toNationalInput(raw: string | null | undefined): string {
  const digits = normalizeDigits(raw);
  return splitSpanish(digits) ?? digits;
}
